import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';

import { FIRST_INDEX } from '@/constants';
import { passwordSchema } from '@/schemas/recoveryPassword.schema';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectChangePasswordError,
  selectChangePasswordPhase,
  selectChangePasswordProgress,
  selectChangePasswordStage,
  selectChangePasswordStatus,
} from '@/store/selectors/changePassword';
import { resetChangePassword } from '@/store/slices/changePassword/changePasswordSlice';
import { changePasswordThunk } from '@/store/thunks/auth/changePasswordThunk';
import type {
  ChangePasswordPhase,
  ChangePasswordStage,
} from '@/store/slices/changePassword/changePasswordSlice';

interface UseChangePasswordState {
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordError: string | undefined;
  globalError: string | undefined;
  stage: ChangePasswordStage | undefined;
  phase: ChangePasswordPhase;
  isRunning: boolean;
  isComplete: boolean;
  isAwaitingConfirmation: boolean;
  isLeaveBlocked: boolean;
  isSubmitDisabled: boolean;
  regeneratedCount: number;
  totalCount: number;
  failedCount: number;
}

interface UseChangePasswordHandlers {
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onSubmit: () => void;
  onConfirmProceed: () => void;
  onCancelConfirmation: () => void;
  onConfirmLeave: () => void;
  onStayHere: () => void;
}

interface UseChangePasswordReturn {
  state: UseChangePasswordState;
  handlers: UseChangePasswordHandlers;
}

export function useChangePassword(): UseChangePasswordReturn {
  const dispatch = useAppDispatch();

  const status = useAppSelector(selectChangePasswordStatus);
  const phase = useAppSelector(selectChangePasswordPhase);
  const progress = useAppSelector(selectChangePasswordProgress);
  const globalError = useAppSelector(selectChangePasswordError);
  const stage = useAppSelector(selectChangePasswordStage);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const isRunning = status === 'running';
  const isComplete = status === 'complete';
  const isAwaitingConfirmation = status === 'awaitingConfirmation';

  // Clear workflow state on entry and exit so a fresh visit starts clean — but
  // never while the rotation thunk is in flight: resetting a live run hides its
  // outcome and would re-enable submit for a concurrent second rotation.
  const isRunningRef = useRef(isRunning);
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);
  useEffect(() => {
    if (!isRunningRef.current) dispatch(resetChangePassword());
    return () => {
      if (!isRunningRef.current) dispatch(resetChangePassword());
    };
  }, [dispatch]);

  // Warn against leaving while the rotation is in flight — beforeunload covers
  // tab close / reload, the router blocker covers in-app navigation.
  useEffect(() => {
    if (!isRunning) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- required by older Chrome/Edge to show the native "leave site?" prompt
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRunning]);

  // Block in-app navigation while the rotation runs; the view renders a
  // stay/leave panel while the blocker is holding the navigation.
  const blocker = useBlocker({
    shouldBlockFn: () => true,
    disabled: !isRunning,
    enableBeforeUnload: false,
    withResolver: true,
  });
  const isLeaveBlocked = blocker.status === 'blocked';

  const onConfirmLeave = useCallback(() => {
    blocker.proceed?.();
  }, [blocker]);

  const onStayHere = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  const parsed = useMemo(
    () => passwordSchema.safeParse({ password, confirmPassword }),
    [password, confirmPassword]
  );

  const passwordError = useMemo(() => {
    if (!hasSubmitted || parsed.success) return undefined;
    return parsed.error.flatten().fieldErrors.password?.[FIRST_INDEX];
  }, [hasSubmitted, parsed]);

  const isSubmitDisabled = isRunning || isComplete || isAwaitingConfirmation || !parsed.success;

  const onPasswordChange = useCallback((value: string): void => {
    setPassword(value);
  }, []);

  const onConfirmPasswordChange = useCallback((value: string): void => {
    setConfirmPassword(value);
  }, []);

  const onTogglePassword = useCallback((): void => {
    setShowPassword((prev) => !prev);
  }, []);

  const onToggleConfirmPassword = useCallback((): void => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const onSubmit = useCallback(() => {
    setHasSubmitted(true);
    const currentParsed = passwordSchema.safeParse({ password, confirmPassword });
    if (!currentParsed.success) return;

    void dispatch(changePasswordThunk({ password: currentParsed.data.password }));
  }, [confirmPassword, dispatch, password]);

  // The flow paused because some keys failed to regenerate; the user accepts
  // losing those files and reruns with the failures allowed through.
  const onConfirmProceed = useCallback(() => {
    const currentParsed = passwordSchema.safeParse({ password, confirmPassword });
    if (!currentParsed.success) return;

    void dispatch(
      changePasswordThunk({ password: currentParsed.data.password, acceptFailedKeys: true })
    );
  }, [confirmPassword, dispatch, password]);

  const onCancelConfirmation = useCallback(() => {
    dispatch(resetChangePassword());
  }, [dispatch]);

  return {
    state: {
      password,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      passwordError,
      globalError,
      stage,
      phase,
      isRunning,
      isComplete,
      isAwaitingConfirmation,
      isLeaveBlocked,
      isSubmitDisabled,
      regeneratedCount: progress.regeneratedCount,
      totalCount: progress.totalCount,
      failedCount: progress.failedCount,
    },
    handlers: {
      onPasswordChange,
      onConfirmPasswordChange,
      onTogglePassword,
      onToggleConfirmPassword,
      onSubmit,
      onConfirmProceed,
      onCancelConfirmation,
      onConfirmLeave,
      onStayHere,
    },
  };
}
